import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";

import { boardsApi } from "../data/boardsApi";
import {
  isProjectsApiEnabled,
  projectsApi,
  type GroupMeta,
  type ProjectDrawingMeta,
  type ProjectFileMeta,
  type ProjectMeta,
} from "../data/projectsApi";
import {
  BoardContextMenu,
  type BoardContextAction,
  type BoardContextMenuState,
} from "./BoardContextMenu";
import { boardFavorites } from "./boardFavorites";
import { ConfirmModal } from "./ConfirmModal";
import { navigateApp } from "./routing";
import { SidebarContextMenu } from "./SidebarContextMenu";

import "./Projects.scss";

type WorkspaceItem =
  | { type: "board"; data: ProjectDrawingMeta }
  | { type: "file"; data: ProjectFileMeta };

type NavKey = "recent" | "drafts" | "favorites" | "project";
type SortKey = "updated" | "created" | "name";
type TypeFilter = "all" | "board" | "image" | "document";
type ViewMode = "grid" | "list";

const decodeFileName = (raw: string) => {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const relativeTime = (ts: number) => {
  const diff = Date.now() - ts;
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;
  if (diff < minute) {
    return "刚刚修改";
  }
  if (diff < hour) {
    return `${Math.floor(diff / minute)} 分钟前修改`;
  }
  if (diff < day) {
    return `${Math.floor(diff / hour)} 小时前修改`;
  }
  if (diff < month) {
    return `${Math.floor(diff / day)} 天前修改`;
  }
  return `${Math.floor(diff / month)} 个月前修改`;
};

const IconSearch = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
    />
  </svg>
);

const IconClock = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
    />
  </svg>
);

const IconDraft = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
    />
  </svg>
);

const IconStar = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
    />
  </svg>
);

const IconFolder = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"
    />
  </svg>
);

const IconPlus = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);

const IconUpload = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"
    />
  </svg>
);

const IconGrid = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z"
    />
  </svg>
);

const IconList = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"
    />
  </svg>
);

const IconBoard = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7v-7zm4-3h2v10h-2V7zm4 6h2v4h-2v-4z"
    />
  </svg>
);

const IconImage = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
    />
  </svg>
);

const IconDoc = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
    />
  </svg>
);

const IconBell = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
    />
  </svg>
);

export const ProjectsWorkspace = ({
  initialProjectId = null,
}: {
  initialProjectId?: string | null;
}) => {
  const [groups, setGroups] = useState<GroupMeta[]>([]);
  const [projects, setProjects] = useState<ProjectMeta[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    initialProjectId,
  );
  const [drawings, setDrawings] = useState<ProjectDrawingMeta[]>([]);
  const [files, setFiles] = useState<ProjectFileMeta[]>([]);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [nav, setNav] = useState<NavKey>("project");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const [imageThumbs, setImageThumbs] = useState<Record<string, string>>({});
  const [boardThumbs, setBoardThumbs] = useState<Record<string, string>>({});
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [contextMenu, setContextMenu] =
    useState<BoardContextMenuState | null>(null);
  const [sidebarMenu, setSidebarMenu] = useState<{
    x: number;
    y: number;
    kind: "group" | "project";
    group?: GroupMeta;
    project?: ProjectMeta;
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    onConfirm: () => Promise<void> | void;
  } | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() =>
    boardFavorites.list(),
  );
  const [toast, setToast] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbUrlsRef = useRef<Record<string, string>>({});
  const boardThumbUrlsRef = useRef<Record<string, string>>({});

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) || null,
    [projects, selectedProjectId],
  );

  const projectsByGroup = useMemo(() => {
    const map = new Map<string, ProjectMeta[]>();
    for (const group of groups) {
      map.set(group.id, []);
    }
    const ungrouped: ProjectMeta[] = [];
    for (const project of projects) {
      if (project.groupId && map.has(project.groupId)) {
        map.get(project.groupId)!.push(project);
      } else {
        ungrouped.push(project);
      }
    }
    return { map, ungrouped };
  }, [groups, projects]);

  const items: WorkspaceItem[] = useMemo(() => {
    const boardItems: WorkspaceItem[] = drawings.map((d) => ({
      type: "board" as const,
      data: d,
    }));
    const fileItems: WorkspaceItem[] = files.map((f) => ({
      type: "file" as const,
      data: f,
    }));
    let list = [...boardItems, ...fileItems];

    if (typeFilter === "board") {
      list = list.filter((i) => i.type === "board");
    } else if (typeFilter === "image") {
      list = list.filter((i) => i.type === "file" && i.data.kind === "image");
    } else if (typeFilter === "document") {
      list = list.filter(
        (i) => i.type === "file" && i.data.kind !== "image",
      );
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) => {
        const name =
          i.type === "board"
            ? i.data.title
            : decodeFileName(i.data.name);
        return name.toLowerCase().includes(q);
      });
    }

    list.sort((a, b) => {
      if (sortKey === "name") {
        const an =
          a.type === "board" ? a.data.title : decodeFileName(a.data.name);
        const bn =
          b.type === "board" ? b.data.title : decodeFileName(b.data.name);
        return an.localeCompare(bn, "zh");
      }
      if (sortKey === "created") {
        return b.data.createdAt - a.data.createdAt;
      }
      return b.data.updatedAt - a.data.updatedAt;
    });

    return list;
  }, [drawings, files, typeFilter, search, sortKey]);

  const refreshSidebar = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setSidebarLoading(true);
    }
    setError("");
    try {
      if (!isProjectsApiEnabled()) {
        setError("未配置云端 API（boards-config.js）");
        setGroups([]);
        setProjects([]);
        return;
      }
      const [g, p] = await Promise.all([
        projectsApi.listGroups(),
        projectsApi.list(),
      ]);
      setGroups(g);
      setProjects(p);
      setSelectedProjectId((prev) => {
        if (prev && p.some((item) => item.id === prev)) {
          return prev;
        }
        if (
          initialProjectId &&
          p.some((item) => item.id === initialProjectId)
        ) {
          return initialProjectId;
        }
        return p[0]?.id || null;
      });
    } catch (err: any) {
      setError(err?.message || "加载失败");
    } finally {
      if (!opts?.silent) {
        setSidebarLoading(false);
      }
    }
  }, [initialProjectId]);

  const refreshContent = useCallback(async (projectId: string) => {
    setContentLoading(true);
    setError("");
    try {
      const [d, f] = await Promise.all([
        projectsApi.listDrawings(projectId),
        projectsApi.listFiles(projectId),
      ]);
      setDrawings(d);
      setFiles(f);
    } catch (err: any) {
      setError(err?.message || "加载项目内容失败");
      setDrawings([]);
      setFiles([]);
    } finally {
      setContentLoading(false);
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Load sidebar once on mount (not on every project click)
  useEffect(() => {
    void refreshSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // Sync selection when URL/project prop changes (e.g. browser back)
  useEffect(() => {
    if (initialProjectId) {
      setSelectedProjectId(initialProjectId);
      setNav("project");
    }
  }, [initialProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setDrawings([]);
      setFiles([]);
      return;
    }
    setNav("project");
    // Update URL without remounting the workspace / reloading the sidebar
    navigateApp(
      { type: "project", projectId: selectedProjectId },
      true,
      { notify: false },
    );
    void refreshContent(selectedProjectId);
  }, [selectedProjectId, refreshContent]);

  useEffect(() => {
    setDescriptionDraft(selectedProject?.description || "");
    setEditingDescription(false);
  }, [selectedProject?.id, selectedProject?.description]);

  // Load image thumbnails
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selectedProjectId) {
        return;
      }
      const images = files.filter((f) => f.kind === "image");
      for (const file of images) {
        if (thumbUrlsRef.current[file.id]) {
          continue;
        }
        try {
          const blob = await projectsApi.fetchFileBlob(
            selectedProjectId,
            file.id,
          );
          if (cancelled) {
            return;
          }
          const url = URL.createObjectURL(blob);
          thumbUrlsRef.current[file.id] = url;
          setImageThumbs((prev) => ({ ...prev, [file.id]: url }));
        } catch {
          // ignore thumb errors
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [files, selectedProjectId]);

  // Load / generate board preview thumbnails
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { getOrCreateBoardPreviewBlob } = await import(
        "../data/boardPreview"
      );
      for (const board of drawings) {
        if (boardThumbUrlsRef.current[board.id]) {
          continue;
        }
        try {
          const blob = await getOrCreateBoardPreviewBlob(board.id);
          if (cancelled) {
            return;
          }
          if (!blob) {
            continue;
          }
          const url = URL.createObjectURL(blob);
          boardThumbUrlsRef.current[board.id] = url;
          setBoardThumbs((prev) => ({ ...prev, [board.id]: url }));
        } catch {
          // ignore preview errors
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [drawings]);

  useEffect(() => {
    return () => {
      Object.values(thumbUrlsRef.current).forEach((url) =>
        URL.revokeObjectURL(url),
      );
      Object.values(boardThumbUrlsRef.current).forEach((url) =>
        URL.revokeObjectURL(url),
      );
      thumbUrlsRef.current = {};
      boardThumbUrlsRef.current = {};
    };
  }, []);

  const selectProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    setNav("project");
  };

  const createGroup = async () => {
    const title = window.prompt("分组名称", "未命名分组");
    if (title === null) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      await projectsApi.createGroup(title.trim() || "未命名分组");
      await refreshSidebar({ silent: true });
    } catch (err: any) {
      setError(err?.message || "创建分组失败");
    } finally {
      setBusy(false);
    }
  };

  const createProjectInGroup = async (groupId: string) => {
    const title = window.prompt("项目名称", "未命名项目");
    if (title === null) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const project = await projectsApi.create({
        title: title.trim() || "未命名项目",
        groupId,
      });
      await refreshSidebar({ silent: true });
      setSelectedProjectId(project.id);
    } catch (err: any) {
      setError(err?.message || "创建项目失败");
    } finally {
      setBusy(false);
    }
  };

  const renameGroup = async (group: GroupMeta) => {
    const title = window.prompt("重命名分组", group.title);
    if (title === null || !title.trim()) {
      return;
    }
    setBusy(true);
    try {
      await projectsApi.updateGroup(group.id, { title: title.trim() });
      await refreshSidebar({ silent: true });
    } catch (err: any) {
      setError(err?.message || "重命名失败");
    } finally {
      setBusy(false);
    }
  };

  const pinGroup = async (group: GroupMeta) => {
    setBusy(true);
    try {
      const minOrder = groups.reduce(
        (min, g) => Math.min(min, g.sortOrder ?? 0),
        group.sortOrder ?? 0,
      );
      await projectsApi.updateGroup(group.id, { sortOrder: minOrder - 1 });
      await refreshSidebar({ silent: true });
      showToast("已置顶分组");
    } catch (err: any) {
      setError(err?.message || "置顶失败");
    } finally {
      setBusy(false);
    }
  };

  const performDeleteGroup = async (group: GroupMeta) => {
    setBusy(true);
    try {
      await projectsApi.removeGroup(group.id);
      await refreshSidebar({ silent: true });
      showToast("已删除分组");
    } catch (err: any) {
      setError(err?.message || "删除分组失败");
    } finally {
      setBusy(false);
      setConfirmModal(null);
    }
  };

  const requestDeleteGroup = (group: GroupMeta) => {
    setConfirmModal({
      title: "删除分组",
      message: `确定删除分组「${group.title}」？分组内项目会保留到未分组。`,
      onConfirm: () => performDeleteGroup(group),
    });
  };

  const renameProject = async (project: ProjectMeta) => {
    const title = window.prompt("重命名项目", project.title);
    if (title === null || !title.trim()) {
      return;
    }
    setBusy(true);
    try {
      await projectsApi.update(project.id, { title: title.trim() });
      await refreshSidebar({ silent: true });
    } catch (err: any) {
      setError(err?.message || "重命名失败");
    } finally {
      setBusy(false);
    }
  };

  const performDeleteProject = async (project: ProjectMeta) => {
    setBusy(true);
    try {
      await projectsApi.remove(project.id);
      if (selectedProjectId === project.id) {
        setSelectedProjectId(null);
      }
      await refreshSidebar({ silent: true });
      showToast("已删除项目");
    } catch (err: any) {
      setError(err?.message || "删除项目失败");
    } finally {
      setBusy(false);
      setConfirmModal(null);
    }
  };

  const requestDeleteProject = (project: ProjectMeta) => {
    setConfirmModal({
      title: "删除项目",
      message: `确定删除项目「${project.title}」？项目内文件会删除，画板会保留到未分类。`,
      onConfirm: () => performDeleteProject(project),
    });
  };

  const saveDescription = async () => {
    if (!selectedProject) {
      return;
    }
    setBusy(true);
    try {
      await projectsApi.update(selectedProject.id, {
        description: descriptionDraft.trim(),
      });
      await refreshSidebar({ silent: true });
      setEditingDescription(false);
    } catch (err: any) {
      setError(err?.message || "保存描述失败");
    } finally {
      setBusy(false);
    }
  };

  const createBoard = async () => {
    if (!selectedProjectId) {
      setError("请先选择一个项目");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const drawing = await projectsApi.createDrawing(
        selectedProjectId,
        "未命名画板",
      );
      navigateApp({
        type: "board",
        boardId: drawing.id,
        projectId: selectedProjectId,
      });
    } catch (err: any) {
      setError(err?.message || "创建画板失败");
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (list: FileList | null) => {
    if (!selectedProjectId || !list?.length) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      for (const file of Array.from(list)) {
        await projectsApi.uploadFile(selectedProjectId, file);
      }
      await refreshContent(selectedProjectId);
    } catch (err: any) {
      setError(err?.message || "上传失败");
    } finally {
      setBusy(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openFile = async (file: ProjectFileMeta) => {
    if (!selectedProjectId) {
      return;
    }
    try {
      const blob = await projectsApi.fetchFileBlob(selectedProjectId, file.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      setError(err?.message || "打开文件失败");
    }
  };

  const deleteFile = async (file: ProjectFileMeta) => {
    if (!selectedProjectId) {
      return;
    }
    if (!window.confirm(`删除「${decodeFileName(file.name)}」？`)) {
      return;
    }
    setBusy(true);
    try {
      await projectsApi.removeFile(selectedProjectId, file.id);
      await refreshContent(selectedProjectId);
    } catch (err: any) {
      setError(err?.message || "删除失败");
    } finally {
      setBusy(false);
    }
  };

  const boardUrl = useCallback(
    (boardId: string) => {
      const url = new URL(window.location.origin + window.location.pathname);
      url.searchParams.set("board", boardId);
      if (selectedProjectId) {
        url.searchParams.set("project", selectedProjectId);
      }
      return url.toString();
    },
    [selectedProjectId],
  );

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const openBoardContextMenu = (
    event: ReactMouseEvent,
    board: ProjectDrawingMeta,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      boardId: board.id,
      boardTitle: board.title,
      favorited: favoriteIds.includes(board.id),
    });
  };

  const onBoardContextAction = async (
    action: BoardContextAction,
    menu: BoardContextMenuState,
  ) => {
    const { boardId, boardTitle } = menu;
    try {
      if (action === "open") {
        navigateApp({
          type: "board",
          boardId,
          projectId: selectedProjectId,
        });
        return;
      }
      if (action === "openTab") {
        window.open(boardUrl(boardId), "_blank", "noopener,noreferrer");
        return;
      }
      if (action === "favorite") {
        const next = boardFavorites.toggle(boardId);
        setFavoriteIds(boardFavorites.list());
        showToast(next ? "已收藏" : "已取消收藏");
        return;
      }
      if (action === "share") {
        const link = boardUrl(boardId);
        await navigator.clipboard.writeText(link);
        showToast("链接已复制");
        return;
      }
      if (action === "duplicate") {
        setBusy(true);
        await boardsApi.duplicate(boardId, {
          title: `${boardTitle} 副本`,
          projectId: selectedProjectId,
        });
        if (selectedProjectId) {
          await refreshContent(selectedProjectId);
        }
        showToast("已创建副本");
        return;
      }
      if (action === "rename") {
        const next = window.prompt("重命名画板", boardTitle);
        if (next === null || !next.trim() || next.trim() === boardTitle) {
          return;
        }
        setBusy(true);
        await boardsApi.update(boardId, { title: next.trim() });
        if (selectedProjectId) {
          await refreshContent(selectedProjectId);
        }
        showToast("已重命名");
        return;
      }
      if (action === "move") {
        if (projects.length < 2) {
          showToast("没有可移动到的其他项目");
          return;
        }
        const options = projects
          .filter((p) => p.id !== selectedProjectId)
          .map((p, i) => `${i + 1}. ${p.title}`)
          .join("\n");
        const answer = window.prompt(
          `移动「${boardTitle}」到哪个项目？输入序号：\n${options}`,
        );
        if (answer === null) {
          return;
        }
        const index = Number(answer) - 1;
        const targets = projects.filter((p) => p.id !== selectedProjectId);
        const target = targets[index];
        if (!target) {
          setError("无效的项目序号");
          return;
        }
        setBusy(true);
        await boardsApi.update(boardId, { projectId: target.id });
        if (selectedProjectId) {
          await refreshContent(selectedProjectId);
        }
        showToast(`已移动到「${target.title}」`);
        return;
      }
      if (action === "delete") {
        if (
          !window.confirm(
            `将画板「${boardTitle}」移至回收站（删除）？此操作不可恢复。`,
          )
        ) {
          return;
        }
        setBusy(true);
        await boardsApi.remove(boardId);
        if (favoriteIds.includes(boardId)) {
          boardFavorites.toggle(boardId);
          setFavoriteIds(boardFavorites.list());
        }
        if (selectedProjectId) {
          await refreshContent(selectedProjectId);
        }
        showToast("已删除画板");
      }
    } catch (err: any) {
      setError(err?.message || "操作失败");
    } finally {
      setBusy(false);
    }
  };

  const itemName = (item: WorkspaceItem) =>
    item.type === "board" ? item.data.title : decodeFileName(item.data.name);

  const visibleItems = useMemo(() => {
    if (nav === "favorites") {
      return items.filter(
        (item) =>
          item.type === "board" && favoriteIds.includes(item.data.id),
      );
    }
    return items;
  }, [items, nav, favoriteIds]);

  const showProjectPane =
    (nav === "project" || nav === "favorites") && Boolean(selectedProject);

  const renderProjectButton = (project: ProjectMeta) => (
    <button
      key={project.id}
      type="button"
      className={`pixso-nav__project${
        project.id === selectedProjectId && nav === "project"
          ? " is-active"
          : ""
      }`}
      onClick={() => selectProject(project.id)}
      onDoubleClick={() => void renameProject(project)}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setSidebarMenu({
          x: e.clientX,
          y: e.clientY,
          kind: "project",
          project,
        });
      }}
    >
      <span className="pixso-nav__project-icon">
        <IconFolder />
      </span>
      <span className="pixso-nav__project-name">{project.title}</span>
    </button>
  );

  return (
    <div className="pixso">
      <aside className="pixso-sidebar">
        <div className="pixso-sidebar__brand">
          <span className="pixso-sidebar__logo">大</span>
          <span className="pixso-sidebar__brand-text">大画板</span>
        </div>

        <nav className="pixso-nav">
          <button
            type="button"
            className={`pixso-nav__item${nav === "recent" ? " is-active" : ""}`}
            onClick={() => setNav("recent")}
          >
            <IconClock />
            <span>最近打开</span>
          </button>
          <button
            type="button"
            className={`pixso-nav__item${nav === "drafts" ? " is-active" : ""}`}
            onClick={() => setNav("drafts")}
          >
            <IconDraft />
            <span>我的草稿</span>
          </button>
          <button
            type="button"
            className={`pixso-nav__item${
              nav === "favorites" ? " is-active" : ""
            }`}
            onClick={() => setNav("favorites")}
          >
            <IconStar />
            <span>我的收藏</span>
          </button>
        </nav>

        <div className="pixso-sidebar__scroll">
          {sidebarLoading && <div className="pixso-muted">加载中…</div>}

          {!sidebarLoading &&
            groups.map((group) => {
              const collapsed = collapsedGroups[group.id];
              const list = projectsByGroup.map.get(group.id) || [];
              return (
                <section key={group.id} className="pixso-team">
                  <div className="pixso-team__head">
                    <div className="pixso-team__toggle">
                      <button
                        type="button"
                        className="pixso-team__chevron-btn"
                        onClick={() =>
                          setCollapsedGroups((prev) => ({
                            ...prev,
                            [group.id]: !prev[group.id],
                          }))
                        }
                        aria-label={collapsed ? "展开" : "折叠"}
                      >
                        <span className="pixso-team__chevron">
                          {collapsed ? "▸" : "▾"}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="pixso-team__title"
                        onClick={() =>
                          setCollapsedGroups((prev) => ({
                            ...prev,
                            [group.id]: !prev[group.id],
                          }))
                        }
                        onDoubleClick={(e) => {
                          e.preventDefault();
                          void renameGroup(group);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSidebarMenu({
                            x: e.clientX,
                            y: e.clientY,
                            kind: "group",
                            group,
                          });
                        }}
                        title="右键更多操作"
                      >
                        {group.title}
                      </button>
                    </div>
                    <div className="pixso-team__actions">
                      <button
                        type="button"
                        className="pixso-icon-btn"
                        title="新建项目"
                        disabled={busy}
                        onClick={() => void createProjectInGroup(group.id)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {!collapsed && (
                    <div className="pixso-team__list">
                      {list.map(renderProjectButton)}
                      {list.length === 0 && (
                        <div className="pixso-muted pixso-muted--sm">
                          暂无项目
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            })}

          {!sidebarLoading && projectsByGroup.ungrouped.length > 0 && (
            <section className="pixso-team">
              <div className="pixso-team__head">
                <span className="pixso-team__title">未分组</span>
              </div>
              <div className="pixso-team__list">
                {projectsByGroup.ungrouped.map(renderProjectButton)}
              </div>
            </section>
          )}
        </div>

        <button
          type="button"
          className="pixso-sidebar__create"
          onClick={() => void createGroup()}
          disabled={busy || sidebarLoading}
        >
          <IconPlus />
          创建分组
        </button>
      </aside>

      <div className="pixso-center">
        <header className="pixso-topbar">
          <div className="pixso-search">
            <IconSearch />
            <input
              ref={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索画板、文件…"
            />
            <kbd>Ctrl F</kbd>
          </div>
          <div className="pixso-topbar__right">
            <button type="button" className="pixso-btn pixso-btn--primary">
              云端已同步
            </button>
            <button type="button" className="pixso-icon-round" title="通知">
              <IconBell />
            </button>
            <div className="pixso-avatar" title="个人空间">
              我
            </div>
          </div>
        </header>

        <div className="pixso-body">
          <main className="pixso-main">
            {error && <div className="pixso-error">{error}</div>}
            {toast && <div className="pixso-toast">{toast}</div>}

            {nav !== "project" && nav !== "favorites" && (
              <div className="pixso-placeholder">
                <h2>
                  {nav === "recent"
                    ? "最近打开"
                    : nav === "drafts"
                      ? "我的草稿"
                      : "我的收藏"}
                </h2>
                <p>从左侧选择项目开始管理画板与文件</p>
              </div>
            )}

            {nav === "favorites" && !selectedProject && !sidebarLoading && (
              <div className="pixso-placeholder">
                <h2>我的收藏</h2>
                <p>先选择一个项目，再在画板上右键收藏</p>
              </div>
            )}

            {nav === "project" && !selectedProject && !sidebarLoading && (
              <div className="pixso-placeholder">
                <h2>选择或创建项目</h2>
                <p>在左侧分组中点击「+」新建项目</p>
              </div>
            )}

            {showProjectPane && selectedProject && (
              <>
                <div className="pixso-project-head">
                  <div className="pixso-project-head__row">
                    <h1
                      className="pixso-project-title"
                      onDoubleClick={() => void renameProject(selectedProject)}
                      title="双击重命名"
                    >
                      {nav === "favorites"
                        ? "我的收藏"
                        : selectedProject.title}
                    </h1>
                    <div className="pixso-project-head__tools">
                      <button
                        type="button"
                        className="pixso-text-btn"
                        onClick={() => void renameProject(selectedProject)}
                      >
                        重命名
                      </button>
                      <button
                        type="button"
                        className="pixso-text-btn pixso-text-btn--danger"
                        onClick={() => requestDeleteProject(selectedProject)}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                  <div className="pixso-project-meta">
                    <span>仅自己可编辑</span>
                    <span className="pixso-dot">·</span>
                    {editingDescription ? (
                      <span className="pixso-desc-edit">
                        <input
                          value={descriptionDraft}
                          onChange={(e) => setDescriptionDraft(e.target.value)}
                          placeholder="添加项目描述"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              void saveDescription();
                            }
                            if (e.key === "Escape") {
                              setEditingDescription(false);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="pixso-text-btn"
                          onClick={() => void saveDescription()}
                        >
                          保存
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="pixso-text-btn"
                        onClick={() => setEditingDescription(true)}
                      >
                        {selectedProject.description || "添加项目描述"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="pixso-actions">
                  <button
                    type="button"
                    className="pixso-btn pixso-btn--primary pixso-btn--lg"
                    onClick={() => void createBoard()}
                    disabled={busy}
                  >
                    <IconPlus />
                    新建画板
                  </button>
                  <button
                    type="button"
                    className="pixso-btn pixso-btn--secondary pixso-btn--lg"
                    onClick={() =>
                      selectedProject.groupId &&
                      void createProjectInGroup(selectedProject.groupId)
                    }
                    disabled={busy || !selectedProject.groupId}
                    title="在同分组下新建项目"
                  >
                    <span className="pixso-folder-badge">
                      <IconFolder />
                    </span>
                    新建项目
                  </button>
                  <button
                    type="button"
                    className="pixso-btn pixso-btn--secondary pixso-btn--lg"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                  >
                    <IconUpload />
                    导入文件
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => void onUpload(e.target.files)}
                  />
                </div>

                <div className="pixso-filters">
                  <select
                    value={typeFilter}
                    onChange={(e) =>
                      setTypeFilter(e.target.value as TypeFilter)
                    }
                  >
                    <option value="all">全部类型</option>
                    <option value="board">画板</option>
                    <option value="image">图片</option>
                    <option value="document">文档</option>
                  </select>
                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value as SortKey)}
                  >
                    <option value="updated">最近修改</option>
                    <option value="created">创建时间</option>
                    <option value="name">名称</option>
                  </select>
                  <div className="pixso-filters__spacer" />
                  <div className="pixso-view-toggle">
                    <button
                      type="button"
                      className={viewMode === "grid" ? "is-active" : ""}
                      onClick={() => setViewMode("grid")}
                      title="网格"
                    >
                      <IconGrid />
                    </button>
                    <button
                      type="button"
                      className={viewMode === "list" ? "is-active" : ""}
                      onClick={() => setViewMode("list")}
                      title="列表"
                    >
                      <IconList />
                    </button>
                  </div>
                </div>

                {contentLoading && (
                  <div className="pixso-muted">加载内容…</div>
                )}

                {!contentLoading && visibleItems.length === 0 && (
                  <div className="pixso-empty-card">
                    <p>
                      {nav === "favorites"
                        ? "还没有收藏的画板，在画板上右键可收藏"
                        : "还没有内容，新建画板或导入文件开始吧"}
                    </p>
                  </div>
                )}

                <div
                  className={
                    viewMode === "grid" ? "pixso-grid" : "pixso-list"
                  }
                >
                  {visibleItems.map((item) => {
                    const name = itemName(item);
                    const isBoard = item.type === "board";
                    const isImage =
                      item.type === "file" && item.data.kind === "image";
                    const thumb = isBoard
                      ? boardThumbs[item.data.id] || null
                      : isImage && imageThumbs[item.data.id]
                        ? imageThumbs[item.data.id]
                        : null;
                    const isFav =
                      isBoard && favoriteIds.includes(item.data.id);

                    return (
                      <article
                        key={`${item.type}-${item.data.id}`}
                        className={`pixso-card${
                          viewMode === "list" ? " pixso-card--list" : ""
                        }${isFav ? " pixso-card--fav" : ""}`}
                        onContextMenu={(e) => {
                          if (isBoard) {
                            openBoardContextMenu(e, item.data);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className="pixso-card__preview"
                          onClick={() => {
                            if (isBoard) {
                              navigateApp({
                                type: "board",
                                boardId: item.data.id,
                                projectId: selectedProjectId,
                              });
                            } else {
                              void openFile(item.data);
                            }
                          }}
                        >
                          {thumb ? (
                            <img src={thumb} alt="" />
                          ) : (
                            <div
                              className={`pixso-card__placeholder pixso-card__placeholder--${
                                isBoard
                                  ? "board"
                                  : isImage
                                    ? "image"
                                    : "doc"
                              }`}
                            >
                              {isBoard ? (
                                <IconBoard />
                              ) : isImage ? (
                                <IconImage />
                              ) : (
                                <IconDoc />
                              )}
                            </div>
                          )}
                        </button>
                        <div className="pixso-card__meta">
                          <span className="pixso-card__type">
                            {isBoard ? (
                              <IconBoard />
                            ) : isImage ? (
                              <IconImage />
                            ) : (
                              <IconDoc />
                            )}
                          </span>
                          <div className="pixso-card__text">
                            <div className="pixso-card__name" title={name}>
                              {isFav ? "★ " : ""}
                              {name}
                            </div>
                            <div className="pixso-card__time">
                              {relativeTime(item.data.updatedAt)}
                            </div>
                          </div>
                          {!isBoard && (
                            <button
                              type="button"
                              className="pixso-card__more"
                              title="删除"
                              disabled={busy}
                              onClick={() => void deleteFile(item.data)}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      <BoardContextMenu
        menu={contextMenu}
        onClose={() => setContextMenu(null)}
        onAction={(action, menu) => void onBoardContextAction(action, menu)}
      />

      <SidebarContextMenu
        open={Boolean(sidebarMenu)}
        x={sidebarMenu?.x || 0}
        y={sidebarMenu?.y || 0}
        items={
          sidebarMenu?.kind === "group"
            ? [
                { id: "rename", label: "重命名" },
                { id: "pin", label: "置顶" },
                { id: "delete", label: "删除", danger: true },
              ]
            : [
                { id: "rename", label: "重命名" },
                { id: "delete", label: "删除", danger: true },
              ]
        }
        onClose={() => setSidebarMenu(null)}
        onSelect={(id) => {
          if (!sidebarMenu) {
            return;
          }
          if (sidebarMenu.kind === "group" && sidebarMenu.group) {
            if (id === "rename") {
              void renameGroup(sidebarMenu.group);
            } else if (id === "pin") {
              void pinGroup(sidebarMenu.group);
            } else if (id === "delete") {
              requestDeleteGroup(sidebarMenu.group);
            }
            return;
          }
          if (sidebarMenu.kind === "project" && sidebarMenu.project) {
            if (id === "rename") {
              void renameProject(sidebarMenu.project);
            } else if (id === "delete") {
              requestDeleteProject(sidebarMenu.project);
            }
          }
        }}
      />

      <ConfirmModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title || ""}
        message={confirmModal?.message || ""}
        busy={busy}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => {
          void confirmModal?.onConfirm();
        }}
      />
    </div>
  );
};
